import React, { useState } from "react";
import axios from "axios";

import { FiUser, FiLock, FiArrowRight, FiEye, FiEyeOff, FiShield } from "react-icons/fi";

import "./LoginPage.css";

const LoginPage = () => {

    const [formData, setFormData] = useState({
        username: "",
        password: "",
        rememberMe: false,
        verify: ""   // honeypot field – must remain empty for real users
    });
    const [showPassword, setShowPassword] = useState(false);
    // Track when the form was first rendered so we can reject submissions
    // that arrive faster than a human could fill the form (bot detection).
    const formLoadTime = React.useRef(Date.now());
    const [status, setStatus] = useState({
        loading: false,
        message: "",
        type: "" // 'success', 'error', 'info', or ''
    });

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Honeypot check – bots fill hidden fields
        if (formData.verify) {
            setStatus({ loading: false, message: "Submission rejected.", type: "error" });
            return;
        }

        // Time-based bot detection – reject if form submitted in < 500 ms
        const elapsed = Date.now() - formLoadTime.current;
        if (elapsed < 500) {
            setStatus({ loading: false, message: "Submission rejected.", type: "error" });
            return;
        }

        setStatus({
            loading: true,
            message: "Authenticating...",
            type: "info"
        });

        try {
            const parms = { username: formData.username, password: formData.password };
            const response = await axios.post("/api/login", parms,
                {
                    headers: { "Content-Type": "application/json" },
                    withCredentials: true,
                    timeout: 6000
                }
            );

            if (response.data.success) {
                sessionStorage.removeItem('justLoggedOut');

                if (formData.rememberMe) {
                    localStorage.setItem("rememberMe", "true");
                } else {
                    localStorage.removeItem("rememberMe");
                }

                setStatus({
                    loading: false,
                    message: "Login successful! Redirecting...",
                    type: "success"
                });

                // if (response.data.token) {
                //     localStorage.setItem("authToken", response.data.token);
                // }

                setTimeout(() => {
                    window.location.href = process.env.PUBLIC_URL + '/';
                }, 2000);
            } else {
                setStatus({
                    loading: false,
                    message: response.data.message || "Login failed. Please check your credentials.",
                    type: "error"
                });
            }
        } catch (error) {
            let errorMessage = "An unexpected error occurred. Please try again.";

            if (error.response) {
                if (error.response.status === 401) {
                    errorMessage = "Invalid username or password.";
                } else if (error.response.status === 429) {
                    errorMessage = "Too many attempts. Please wait a moment.";
                } else if (error.response.data?.message) {
                    errorMessage = error.response.data.message;
                }
            } else if (error.request) {
                errorMessage = "Unable to connect to server. Please check your connection.";
            }

            setStatus({
                loading: false,
                message: errorMessage,
                type: "error"
            });
        }
    };

    return (
        <React.Fragment>
            <div className="login-page">
                <div className="bento-container">
                    <div className="bento-card bento-card--large bento-card--prussian">
                        <div className="bento-card__pattern pattern-dots">
                            
                        </div>
                        <h2 className="login-card-title"><FiShield />   FDXVA Secure Login <hr /></h2>
                        <div className="bento-card__content">

                            <form onSubmit={handleSubmit} className="login-form">

                                {/* ── Honeypot (hidden from real users via CSS) ── */}
                                <div className="hp-field" aria-hidden="true">
                                    <input
                                        type="text"
                                        name="verify"
                                        value={formData.honeypot}
                                        onChange={handleInputChange}
                                        tabIndex="-1"
                                        autoComplete="off"
                                    />
                                </div>

                                {/* ── Username ── */}
                                <div className="input-group">
                                    <div className="input-wrapper">
                                        <FiUser className="input-icon" />
                                        <input
                                            type="text"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleInputChange}
                                            placeholder="Username"
                                            disabled={status.loading}
                                            autoComplete="username"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* ── Password ── */}
                                <div className="input-group">
                                    <div className="input-wrapper">
                                        <FiLock className="input-icon" />
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            placeholder="Password"
                                            disabled={status.loading}
                                            autoComplete="current-password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(prev => !prev)}
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                            tabIndex="-1"
                                        >
                                            {showPassword ? <FiEyeOff /> : <FiEye />}
                                        </button>
                                    </div>
                                </div>

                                {/* ── Remember me + Forgot password ── */}
                                <div className="login-options">
                                    <label className="remember-me">
                                        <input
                                            type="checkbox"
                                            name="rememberMe"
                                            checked={formData.rememberMe}
                                            onChange={handleInputChange}
                                            disabled={status.loading}
                                        />
                                        <span>Remember me</span>
                                    </label>
                                    <a
                                        href="/public/forms/reset_password.php"
                                        className="forgot-link"
                                        tabIndex={status.loading ? -1 : 0}
                                    >
                                        Forgot password?
                                    </a>
                                </div>

                                {/* ── Status / spinner ── */}
                                {status.message && (
                                    <div className={`status-message status-message--${status.type}`}>
                                        {status.loading && <span className="spinner"></span>}
                                        {status.message}
                                    </div>
                                )}

                                {/* ── Submit button ── */}
                                <button
                                    type="submit"
                                    className="login-button"
                                    disabled={status.loading}
                                >
                                    {status.loading
                                        ? <><span className="btn-spinner"></span> Signing In...</>
                                        : <>Sign In <FiArrowRight className="button-icon" /></>
                                    }
                                </button>

                                {/* ── Sign up link ── */}
                                <p className="signup-prompt">
                                    Not a member?{" "}
                                    <a href="/public/forms/join1.php" className="signup-link">
                                        Sign up
                                    </a>
                                </p>

                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default LoginPage;
